import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { ImageIcon, FileText, Search, Upload, Loader2, Film, Music, X } from 'lucide-react';
import { useClient } from '../../contexts/ClientContext';
import mediaService from '../../services/mediaService';
import AuthenticatedImage from './AuthenticatedImage';
import { cn } from '@/lib/utils';

interface MediaFile {
  id: string;
  client_id?: string;
  user_id: string;
  filename: string;
  original_filename: string;
  file_size: number;
  mime_type: string;
  media_type: string;
  title?: string;
  description?: string;
  tags: string[];
  folder?: string;
  processing_status: string;
  is_public: boolean;
  file_url?: string;
  thumbnail_url?: string;
  ar_viewer_url?: string;
  width?: number;
  height?: number;
  created_at: string;
  updated_at: string;
}

interface MediaFileSelectorProps {
  onSelect: (file: MediaFile) => void;
  selectedFile?: MediaFile | null;
  acceptedTypes?: string[];
  placeholderText?: string;
  className?: string;
  variant?: 'outline' | 'ghost' | 'dashed' | 'clean';
}

const FileTypeIcon = ({ mediaType, className }: { mediaType: string, className?: string }) => {
  const defaultClassName = "h-6 w-6 text-muted-foreground";
  switch (mediaType) {
    case 'image': return <ImageIcon className={cn(defaultClassName, className)} />;
    case 'video': return <Film className={cn(defaultClassName, className)} />;
    case 'audio': return <Music className={cn(defaultClassName, className)} />;
    case 'other': return (
      <svg className={cn(defaultClassName, className)} fill="currentColor" viewBox="0 0 20 20">
        <path d="M10 2L3 7v6l7 5 7-5V7l-7-5zM8.5 8.5L10 7l1.5 1.5L10 10 8.5 8.5z" />
      </svg>
    );
    default: return <FileText className={cn(defaultClassName, className)} />;
  }
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

export const MediaFileSelector: React.FC<MediaFileSelectorProps> = ({
  onSelect,
  selectedFile,
  acceptedTypes = ['image', 'video', 'audio', 'other'],
  placeholderText = "Select a media file",
  className,
  variant = 'outline'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [allMediaFiles, setAllMediaFiles] = useState<MediaFile[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [totalFiles, setTotalFiles] = useState(0);
  const { currentClient } = useClient();

  // Reset pagination when dialog opens
  useEffect(() => {
    if (isOpen && currentClient) {
      setAllMediaFiles([]);
      setCurrentPage(1);
      setHasNextPage(true);
      setTotalFiles(0);
      fetchMediaFiles(1, true);
    }
  }, [isOpen, currentClient]);

  // Filter files when search term or accepted types change
  useEffect(() => {
    const filtered = allMediaFiles.filter(file => 
      acceptedTypes.includes(file.media_type) &&
      (searchTerm === '' || 
        file.original_filename.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (file.title && file.title.toLowerCase().includes(searchTerm.toLowerCase())))
    );
    setMediaFiles(filtered);
  }, [searchTerm, acceptedTypes, allMediaFiles]);

  // Reset search and reload when search term changes
  useEffect(() => {
    if (isOpen && currentClient) {
      const timeoutId = setTimeout(() => {
        setAllMediaFiles([]);
        setCurrentPage(1);
        setHasNextPage(true);
        fetchMediaFiles(1, true);
      }, 300); // Debounce search

      return () => clearTimeout(timeoutId);
    }
  }, [searchTerm]);

  const fetchMediaFiles = async (page: number = 1, reset: boolean = false) => {
    if (!currentClient || (!hasNextPage && !reset)) return;
    
    if (reset) {
      setIsLoading(true);
    } else {
      setIsLoadingMore(true);
    }

    try {
      const response = await mediaService.getMediaFiles(
        page, 
        50, // Load 50 files per page
        searchTerm || undefined, 
        currentClient.id
      );
      
      const sortedFiles = response.files.sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

      if (reset) {
        setAllMediaFiles(sortedFiles);
      } else {
        setAllMediaFiles(prev => [...prev, ...sortedFiles]);
      }

      setCurrentPage(page);
      setHasNextPage(response.has_next);
      setTotalFiles(response.total);
    } catch (error) {
      console.error('Error fetching media files:', error);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const loadMoreFiles = () => {
    if (!isLoadingMore && hasNextPage) {
      fetchMediaFiles(currentPage + 1, false);
    }
  };

  const handleFileSelect = (file: MediaFile) => {
    onSelect(file);
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {variant === 'dashed' ? (
          <div 
            className={cn(
              "w-full flex items-center justify-center cursor-pointer border-2 border-dashed border-border/80 bg-transparent hover:border-primary/80 hover:bg-primary/10 transition-colors duration-200 rounded-xl",
              className
            )}
          >
            {selectedFile ? (
              <div className="flex items-center gap-3 w-full p-2">
                <div className="flex-shrink-0 h-10 w-10 rounded-md bg-muted flex items-center justify-center">
                  {(selectedFile.media_type === 'image' || 
                    (selectedFile.media_type === 'other' && (selectedFile.mime_type === 'model/gltf-binary' || selectedFile.original_filename?.endsWith('.glb')))) 
                    && selectedFile.thumbnail_url ? (
                    <AuthenticatedImage
                      src={selectedFile.media_type === 'other' ? `/api/v1/media/${selectedFile.id}/thumbnail` : selectedFile.thumbnail_url}
                      alt={selectedFile.original_filename}
                      className="h-full w-full object-cover rounded-md"
                      clientId={currentClient?.id}
                    />
                  ) : (
                    <FileTypeIcon mediaType={selectedFile.media_type} />
                  )}
                </div>
                <div className="flex-grow overflow-hidden">
                  <p className="text-sm font-medium truncate">{selectedFile.original_filename}</p>
                  <p className="text-xs text-muted-foreground">{selectedFile.media_type}</p>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center text-center p-4">
                <Upload className="h-6 w-6 text-primary mb-2" />
                <span className="text-sm text-muted-foreground">{placeholderText}</span>
              </div>
            )}
          </div>
        ) : variant === 'clean' ? (
          <div 
            className={cn(
              "w-full flex items-center justify-center cursor-pointer bg-transparent hover:bg-primary/10 transition-colors duration-200 rounded-xl",
              className
            )}
          >
            {selectedFile ? (
              <div className="flex items-center gap-3 w-full p-2">
                <div className="flex-shrink-0 h-10 w-10 rounded-md bg-muted flex items-center justify-center">
                  {(selectedFile.media_type === 'image' || 
                    (selectedFile.media_type === 'other' && (selectedFile.mime_type === 'model/gltf-binary' || selectedFile.original_filename?.endsWith('.glb')))) 
                    && selectedFile.thumbnail_url ? (
                    <AuthenticatedImage
                      src={selectedFile.media_type === 'other' ? `/api/v1/media/${selectedFile.id}/thumbnail` : selectedFile.thumbnail_url}
                      alt={selectedFile.original_filename}
                      className="h-full w-full object-cover rounded-md"
                      clientId={currentClient?.id}
                    />
                  ) : (
                    <FileTypeIcon mediaType={selectedFile.media_type} />
                  )}
                </div>
                <div className="flex-grow overflow-hidden">
                  <p className="text-sm font-medium truncate">{selectedFile.original_filename}</p>
                  <p className="text-xs text-muted-foreground">{selectedFile.media_type}</p>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center text-center p-4">
                <Upload className="h-6 w-6 text-primary mb-2" />
                <span className="text-sm text-muted-foreground">{placeholderText}</span>
              </div>
            )}
          </div>
        ) : (
          <Button 
            variant={variant}
            className={cn(
              "w-full justify-start text-left h-auto py-2 px-3",
              className
            )}
          >
            {selectedFile ? (
              <div className="flex items-center gap-3 w-full">
                <div className="flex-shrink-0 h-10 w-10 rounded-md bg-muted flex items-center justify-center">
                  {(selectedFile.media_type === 'image' || 
                    (selectedFile.media_type === 'other' && (selectedFile.mime_type === 'model/gltf-binary' || selectedFile.original_filename?.endsWith('.glb')))) 
                    && selectedFile.thumbnail_url ? (
                    <AuthenticatedImage
                      src={selectedFile.media_type === 'other' ? `/api/v1/media/${selectedFile.id}/thumbnail` : selectedFile.thumbnail_url}
                      alt={selectedFile.original_filename}
                      className="h-full w-full object-cover rounded-md"
                      clientId={currentClient?.id}
                    />
                  ) : (
                    <FileTypeIcon mediaType={selectedFile.media_type} />
                  )}
                </div>
                <div className="flex-grow overflow-hidden">
                  <p className="text-sm font-medium truncate">{selectedFile.original_filename}</p>
                  <p className="text-xs text-muted-foreground">{selectedFile.media_type}</p>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Upload className="h-4 w-4" />
                {placeholderText}
              </div>
            )}
          </Button>
        )}
      </DialogTrigger>
      
      <DialogContent className="max-w-5xl w-[90vw] h-[80vh] flex flex-col p-0">
        <DialogHeader className="p-6 border-b">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-2xl font-bold">Media Library</DialogTitle>
            {totalFiles > 0 && (
              <div className="text-sm text-muted-foreground">
                Showing {mediaFiles.length} of {totalFiles} files
              </div>
            )}
          </div>
          <div className="relative mt-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              placeholder={`Search in ${acceptedTypes.join(', ')}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-12 text-lg bg-background rounded-full focus-visible:ring-primary/50"
            />
          </div>
        </DialogHeader>
        
        <div 
          className="flex-grow overflow-y-auto p-6"
          onScroll={(e) => {
            const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
            // Load more when user scrolls to within 200px of bottom
            if (scrollHeight - scrollTop - clientHeight < 200 && hasNextPage && !isLoadingMore) {
              loadMoreFiles();
            }
          }}
        >
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
            </div>
          ) : mediaFiles.length > 0 ? (
            <>
              <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3">
                {mediaFiles.map((file) => (
                  <Card 
                    key={file.id} 
                    className={cn(
                      "cursor-pointer transition-all duration-200 group relative overflow-hidden rounded-xl shadow-sm border-border/50",
                      selectedFile?.id === file.id && 'ring-2 ring-primary ring-offset-2 ring-offset-background'
                    )}
                    onClick={() => handleFileSelect(file)}
                  >
                    <CardContent className="p-0">
                      <div className="aspect-square bg-muted flex items-center justify-center relative">
                        {(file.media_type === 'image' || 
                          (file.media_type === 'other' && (file.mime_type === 'model/gltf-binary' || file.original_filename?.endsWith('.glb')))) 
                          && file.thumbnail_url ? (
                          <AuthenticatedImage
                            src={file.media_type === 'other' ? `/api/v1/media/${file.id}/thumbnail` : file.thumbnail_url}
                            alt={file.title || file.original_filename}
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                            clientId={currentClient?.id}
                          />
                        ) : (
                          <FileTypeIcon mediaType={file.media_type} className="h-10 w-10" />
                        )}
                        <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      <div className="p-3 bg-card/80 backdrop-blur-sm">
                        <h4 className="font-semibold text-sm truncate">{file.title || file.original_filename}</h4>
                        <div className="flex items-center justify-between text-xs text-muted-foreground mt-1">
                          <span>{formatFileSize(file.file_size)}</span>
                          <Badge variant="secondary" className="capitalize">{file.media_type}</Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              
              {/* Loading more indicator */}
              {isLoadingMore && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <span className="ml-2 text-muted-foreground">Loading more files...</span>
                </div>
              )}
              
              {/* Load more button (fallback for users who prefer clicking) */}
              {hasNextPage && !isLoadingMore && (
                <div className="flex items-center justify-center py-8">
                  <Button 
                    variant="outline" 
                    onClick={loadMoreFiles}
                    className="flex items-center gap-2"
                  >
                    <Upload className="h-4 w-4" />
                    Load More Files
                  </Button>
                </div>
              )}
              
              {/* End of results indicator */}
              {!hasNextPage && mediaFiles.length > 0 && (
                <div className="flex items-center justify-center py-8">
                  <div className="text-sm text-muted-foreground bg-muted/50 px-4 py-2 rounded-full">
                    All files loaded ({mediaFiles.length} total)
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mb-6">
                <X className="h-10 w-10 text-muted-foreground" />
              </div>
              <h3 className="font-bold text-2xl mb-2">No Files Found</h3>
              <p className="text-muted-foreground max-w-sm">
                {searchTerm ? `No files matched your search for "${searchTerm}".` : 'Your media library for this category is empty.'}
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};